import { MouseEvent, useEffect, useRef, useState } from 'react';
import braintree from 'braintree-web';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faApple } from '@fortawesome/free-brands-svg-icons';
import Button, { ButtonProps } from '@v2/shared/components/Button';
import { reportError } from '@v2/shared/lib/errors/reportError';
import { PaymentConfig } from '@v2/shared/lib/types';
import usePayment from '../usePayment';
import createBraintreeSession from '../createBraintreeSession';
import { PrimaryWalletReveal, REVEALED } from '../useWalletReveal';
import { SubmitPayload, PaymentCart, WalletShape, WALLET_SHAPE_CLASSES } from '../types';

type Props = {
  payment: PaymentConfig;
  /** The order as it stands before the sheet opens: one line, the selected format. */
  cart: PaymentCart;
  submitPayload: SubmitPayload;
  /**
   * This button's gate in the row it sits in — `reveal.primary`, which is open from the
   * start. Passing it is what lets the wallets below this one know when to open theirs.
   * Left out, the button shows itself as soon as it is ready, which is the same thing.
   */
  reveal?: PrimaryWalletReveal;
  /** The corner the row is drawn with — see `WalletShape`. */
  shape?: WalletShape;
  /**
   * That this button is in a placement with no room for the full label — the PDP's
   * floating bar, which puts it in one of two columns beside Add to Cart. It then reads
   * ` Pay` rather than `Buy with  Pay`.
   *
   * A property of the placement and not of the screen. The legacy button keyed the same
   * distinction off `hidden-xs-down`, which is a fair approximation only because the
   * floating bar is itself phone-only — in the buy box directly above it, on the same
   * phone, the label has a full-width button to sit in and should be whole.
   */
  compact?: boolean;
};

/**
 * The row's shape, as `Button` takes it.
 *
 * `square` is not a contradiction: it is the one shape that emits no radius class of its
 * own, which is what lets the token beside it be the only one on the element. `Button`
 * composes its class list with clsx rather than twMerge, so a radius passed as a className
 * would sit *alongside* the shape's and the winner would be whichever the compiled sheet
 * emits last — `rounded-full`, always, whatever the caller asked for.
 */
const BUTTON_CORNERS: Record<WalletShape, Pick<ButtonProps, 'shape' | 'className'>> = {
  pill: { shape: 'pill' },
  rounded: { shape: 'square', className: WALLET_SHAPE_CLASSES.rounded },
};

/**
 * Apple Pay, as an express checkout: one tap replaces the cart and both address forms.
 *
 * Shared rather than branded — nothing here is DiscountMags-specific. Everything that
 * differs between sites arrives in `payment`: the Braintree token, the merchant name
 * the sheet prints, and the one country we will ship to.
 *
 * It renders **nothing at all** until Braintree, the device and the wallet have all
 * said yes. That is the whole availability story: no placeholder, no disabled button,
 * no error. A visitor on Windows, or on a Mac with no card in Wallet, should see the
 * ordinary Add to Cart button and no evidence this ever considered rendering.
 *
 * Sharing a row with another wallet costs it nothing. It is the ranking wallet, so it
 * draws as soon as it is ready and the others wait for it instead — see
 * `useWalletReveal` for why the waiting only runs that way.
 *
 * The sheet's callbacks are the part worth reading carefully. They are registered once,
 * when the button is clicked, and they close over the cart as it was at that moment —
 * so the running total has to be read from a ref rather than from state, or every
 * recalculation would be applied to the cart the sheet opened with and the totals would
 * stop moving after the first address. That is what `cartRef` is for, and it is the
 * same shape headless settled on.
 */
export default function ApplePay({
  payment,
  cart: initialCart,
  submitPayload,
  reveal = REVEALED,
  shape = 'pill',
  compact = false,
}: Props) {
  const [isReady, setIsReady] = useState(false);
  /**
   * That the question has been answered, as opposed to answered yes. Separate from
   * `isReady` because the row is waiting on the answer either way — see `useWalletReveal`.
   */
  const [hasSettled, setHasSettled] = useState(false);
  /**
   * The page's device data collection, still running. A ref and not state because
   * nothing here renders out of it — it is read once, by the sheet, after it has been
   * authorized.
   */
  const deviceData = useRef<Promise<string | null> | null>(null);
  const [applePayInstance, setApplePayInstance] = useState<any>(null);
  const [cart, setCart] = useState<PaymentCart>(initialCart);
  const cartRef = useRef<PaymentCart>(cart);
  /**
   * The sheet this button currently has up, if it has one — the reentrancy guard, and
   * the whole of it.
   *
   * WebKit allows one active payment session per *document*, and `begin()` on a second
   * one throws `InvalidAccessError: Page already has an active payment session.` That is
   * not a hypothetical: a customer fast double-tapped this button on a product page and
   * the second tap threw, which is the Airbrake group this ref exists to close. Both taps
   * arrive in the same task and the handler has no await before `begin()`, so the second
   * one gets all the way there before the first has done anything asynchronous — nothing
   * about the ordinary flow slows it down enough to matter.
   *
   * A ref and not state deliberately. It is read at the top of a click handler and must
   * be true *in that handler*, and a `setState` would not have committed yet when the
   * second tap of a double-tap arrives — the render that disabled the button would be
   * queued behind both clicks. Nothing renders out of this, so nothing needs to.
   *
   * It is only as good as the last place that clears it: a sheet that ends without
   * `endSheet` being called leaves the button permanently dead, which is a far worse bug
   * than the one being fixed. Every terminal path in `payWithApplePay` goes through it.
   */
  const openSheet = useRef<any>(null);
  const {
    calculateShipping,
    calculateTaxes,
    calculateCartTotal,
    formatAmount,
    getSelectableShippingOptions,
    getSelectedShippingOption,
    getShippingOptionDescription,
  } = usePayment();

  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  useEffect(() => {
    // Before the first await, so the deadline the other wallets are waiting against
    // starts when this one starts rather than when the row was mounted.
    reveal.reportPending();

    if (!isApplePaySupported()) {
      setHasSettled(true);

      return;
    }

    const initialize = async () => {
      try {
        const session = await createBraintreeSession(payment.clientToken);
        const instance: any = await braintree.applePay.create({ client: session.client });

        if (await hasUsableCard(instance.merchantIdentifier)) {
          deviceData.current = session.deviceData;
          setApplePayInstance(instance);
          setIsReady(true);
        }
      } catch (error) {
        // Console only, deliberately: the fallback for every failure here is the
        // Add to Cart button beside it, which is already on the page.
        console.error('Could not initialize Apple Pay', error);
      } finally {
        // Every way out of the setup ends here — a button to draw, a wallet with no
        // card in it, a gateway that would not answer. They are all the same answer as
        // far as the row is concerned: this wallet is done.
        setHasSettled(true);
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The row's half of the handshake, reported from the state transition rather than
   * from the setup itself. `hasSettled` only ever turns true once, so this runs once,
   * however many times the setup does.
   */
  useEffect(() => {
    if (hasSettled) {
      reveal.reportSettled();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSettled]);

  /**
   * Checks the shape before trusting the answer. Google's shopping crawler (Storebot-Google)
   * defines a `window.ApplePaySession` that has no `supportsVersion`, and calling it anyway threw
   * inside the setup effect, which took the whole product page down to its error boundary.
   * Anything that is not a working ApplePaySession is simply a device without Apple Pay.
   */
  function isApplePaySupported(): boolean {
    const session = window.ApplePaySession;

    if (!session || typeof session.supportsVersion !== 'function' || typeof session.canMakePayments !== 'function') {
      return false;
    }

    try {
      return Boolean(session.supportsVersion(3) && session.canMakePayments());
    } catch {
      return false;
    }
  }

  /**
   * Not just "does this device do Apple Pay" but "is there a card in this wallet that
   * works with our merchant account". Without it the button shows for anyone on Safari
   * and the sheet opens onto nothing.
   *
   * `canMakePaymentsWithActiveCard` is the call that has always answered this, and it is
   * the slowest thing in the setup — a native call with a round trip to Apple inside it,
   * which is invisible in the network panel and is why it took so long to find. Apple
   * deprecated it in favour of `applePayCapabilities`, which answers the same question
   * and does not have to go and ask.
   *
   * So the new call is asked first and its answer is only taken at its word when it is a
   * yes. `paymentCredentialStatusUnknown` is a yes: it means the device will not say,
   * which Apple's own guidance is to treat as a card being there. A no is not taken at
   * its word, because there are open reports of this call returning
   * `paymentCredentialsUnavailable` on devices with provisioned cards and
   * `applePayUnsupported` inside browsers that do support Apple Pay — so a no falls
   * through to the deprecated call for a second opinion before the button is withheld.
   *
   * That ordering is what makes this worth doing. The visitor who has a card — the one
   * who was being made to wait — is answered by the fast call and never reaches the slow
   * one. The visitor who pays both costs is the one who is going to be shown nothing
   * either way, and has nothing to wait for.
   */
  async function hasUsableCard(merchantIdentifier: string): Promise<boolean> {
    const wallet = window.ApplePaySession;

    if (typeof wallet.applePayCapabilities === 'function') {
      try {
        const status = (await wallet.applePayCapabilities(merchantIdentifier))?.paymentCredentialStatus;

        if (status === 'paymentCredentialsAvailable' || status === 'paymentCredentialStatusUnknown') {
          return true;
        }
      } catch (error) {
        console.error('Could not read the Apple Pay capabilities', error);
      }
    }

    return Boolean(await wallet.canMakePaymentsWithActiveCard(merchantIdentifier));
  }

  /** The Total line, restated after every recalculation. */
  function totalFor(current: PaymentCart) {
    return { label: payment.merchantName, amount: formatAmount(calculateCartTotal(current)) };
  }

  function buildOrderItems(current: PaymentCart) {
    const items = [{ label: 'Subtotal', type: 'final', amount: formatAmount(current.total_items_price) }];

    if (current.total_discounts > 0) {
      items.push({ label: 'Discount', type: 'final', amount: formatAmount(-current.total_discounts) });
    }

    if (current.tax_total_amount > 0) {
      items.push({ label: 'Estimated Tax', type: 'final', amount: formatAmount(current.tax_total_amount) });
    }

    // Always a Shipping line, whether or not the sheet offered a method to choose — for
    // most of what this site sells there is nothing to choose and this line, reading
    // `Free Shipping`, is the only place the visitor is told so. It is what the legacy
    // button prints, down to dropping the `Shipping:` prefix when nothing is charged.
    const shippingOption = getSelectedShippingOption(current);

    items.push({
      label: shippingOption.cost > 0 ? `Shipping: ${shippingOption.name}` : 'Free Shipping',
      type: 'final',
      amount: formatAmount(shippingOption.cost),
    });

    return items;
  }

  async function payWithApplePay(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();

    // Before the reset below, not after: a tap that lands while a sheet is up must leave
    // that sheet's order alone. Putting the guard second would let the second tap of a
    // double-tap rewind `cartRef` to list price underneath the sheet the first tap
    // opened, and the address the visitor is part way through entering would then be
    // priced against the wrong cart.
    if (!applePayInstance || openSheet.current) {
      return;
    }

    // Back to list price and no address: the sheet may have been opened, abandoned and
    // opened again, and the second one must not start from the first one's answers.
    setCart(initialCart);
    cartRef.current = initialCart;

    const paymentRequest = applePayInstance.createPaymentRequest({
      total: totalFor(initialCart),
      requiredShippingContactFields: ['postalAddress', 'name', 'email'],
      requiredBillingContactFields: ['name', 'postalAddress'],
      lineItems: buildOrderItems(initialCart),
    });

    const session = new window.ApplePaySession(3, paymentRequest);

    openSheet.current = session;

    /**
     * This sheet is over, however it ended — authorized, abandoned, or torn down because
     * the gateway would not vouch for us. Releases the guard so the next tap is a
     * legitimate retry rather than a no-op.
     *
     * Safe to call twice: `onpaymentauthorized` already completes the sheet twice when
     * the cart write fails after a successful authorization.
     */
    const endSheet = () => {
      // Only while it is still this sheet's guard to release. A sheet now ends late as
      // well as early — a cancelled one whose pricing call is still in flight comes back
      // here seconds afterwards — and by then the visitor may have opened another, whose
      // guard is not ours to clear.
      if (openSheet.current === session) {
        openSheet.current = null;
      }
    };

    // Apple's own event for "the visitor swiped it away", and the only terminal path that
    // is not something this component does to the sheet itself. Unwired until now, because
    // until now nothing here cared how a sheet ended.
    session.oncancel = endSheet;

    /**
     * A call into the sheet made after an await — which is a call that may arrive too late.
     *
     * Every `ApplePaySession` method throws `InvalidAccessError: The object does not
     * support the operation or argument.` once the session has left the state that method
     * requires, and by the time one of these handlers has awaited an api the sheet may be
     * gone. Cancelling mid-calculation is the ordinary way to get there: tap Apple Pay,
     * enter an address, swipe the sheet away before shipping and tax come back. The
     * pricing calls are plain fetches and know nothing about the sheet, so they answer as
     * usual — into a session that has been cancelled for a second or two.
     *
     * The handlers are all `async`, so that synchronous throw becomes a rejected promise
     * nobody awaits: an unhandled rejection, which is how it reached Airbrake rather than
     * anyone who could act on it. It is the same trap `begin()` is wrapped against below.
     *
     * Logged rather than reported. A sheet that is over is over and there is nothing for
     * anyone to do about it; the failure worth seeing is whatever put us here, which
     * `sheetFailed` sends. It does release the guard, though — a sheet that ended without
     * firing `oncancel` would otherwise leave `openSheet` holding a dead session, and the
     * button dead with it for the rest of the page.
     *
     * It takes the reply and nothing else. Inside a `try`, whatever the reply is handed —
     * the totals, the line items, the options list — is computed on the line above, so a
     * bug in *our* arithmetic still falls to the handler's own catch and the sheet is
     * still answered. Only the reply itself is allowed to be lost. In a `catch` there is
     * no further recovery to fall to, so there the whole call goes inside.
     *
     * Only the calls that follow an await want this. The one answering an address outside
     * our single shipping country runs before anything is awaited, so its sheet is still
     * there by definition — guarding it would only hide a real bug.
     */
    const tellSheet = (reply: () => void): boolean => {
      try {
        reply();

        return true;
      } catch (error) {
        endSheet();
        console.error('Could not reach the Apple Pay sheet', error);

        return false;
      }
    };

    /**
     * A step of the sheet that failed: the merchant we could not validate, the address we
     * could not price, the authorized payment we could not charge.
     *
     * These have only ever been written to the console, so the reason an Apple Pay sheet
     * failed was visible to nobody but the visitor it failed for. What did reach Airbrake
     * was the `InvalidAccessError` thrown by the recovery call afterwards, which says
     * nothing at all about the cause — three notices about an abort, and no way to tell
     * whether the gateway was down or a postcode was bad.
     *
     * Reported whatever became of the sheet. Nothing a cancelled sheet does arrives here:
     * a session call that finds the sheet gone is caught by `tellSheet` and never reaches
     * one of these catches, so what is left is a gateway, a pricing api or a cart write
     * that genuinely failed. Staying quiet because the visitor had given up and swiped the
     * sheet away would go silent in proportion to how bad the outage was.
     */
    const sheetFailed = (step: string, message: string, error: unknown) => {
      console.error(message, error);
      reportError(error, { source: 'applePay', step });
    };

    session.onvalidatemerchant = async (event: any) => {
      try {
        const merchantSession = await applePayInstance.performValidation({
          validationURL: event.validationURL,
          displayName: payment.merchantName,
        });

        // A merchant session the sheet will not take leaves it spinning on "Contacting
        // merchant", so it is torn down rather than left — the same recovery the catch
        // below does, and the reason `tellSheet` reports whether it got through.
        if (!tellSheet(() => session.completeMerchantValidation(merchantSession))) {
          tellSheet(() => session.abort());
        }
      } catch (error) {
        // Split from the call above so this catch means what its message says. Folded
        // together, a sheet that was merely gone arrived here as "could not validate the
        // merchant" and would now be reported as a gateway failure that never happened.
        sheetFailed('merchantValidation', 'Could not validate the Apple Pay merchant', error);
        endSheet();
        tellSheet(() => session.abort());
      }
    };

    session.onshippingcontactselected = async (event: any) => {
      // Dropping the previously chosen method is deliberate: it belonged to the old
      // address and may not even be offered at the new one.
      let current: PaymentCart = { ...cartRef.current, selected_shipping_option: undefined };
      const countryIso2 = String(event.shippingContact.countryCode).toUpperCase();

      if (countryIso2 !== payment.shippingCountry) {
        session.completeShippingContactSelection(
          window.ApplePaySession.STATUS_INVALID_SHIPPING_POSTAL_ADDRESS,
          [],
          totalFor(current),
          buildOrderItems(current)
        );

        return;
      }

      const address = {
        city: event.shippingContact.locality,
        state: event.shippingContact.administrativeArea,
        zip: event.shippingContact.postalCode,
        country_iso2: countryIso2,
      };

      try {
        current = await calculateShipping(current, address);
        current = await calculateTaxes(current, address);

        // Only the methods the api actually priced, so an order that does not ship is
        // offered none and the sheet presents no chooser at all — shipping method
        // selection is optional in Apple Pay, and this is what the legacy button has
        // always done. The Free Shipping line `buildOrderItems` prints under the (i)
        // icon is where the visitor is told delivery is free. Google's sheet cannot do
        // this: it requires at least one option, which is why the two lists differ.
        const shippingMethods = getSelectableShippingOptions(current).map((option) => ({
          identifier: option.id,
          label: option.name,
          detail: getShippingOptionDescription(option, true),
          amount: formatAmount(option.cost),
        }));
        const total = totalFor(current);
        const orderItems = buildOrderItems(current);

        tellSheet(() =>
          session.completeShippingContactSelection(
            window.ApplePaySession.STATUS_SUCCESS,
            shippingMethods,
            total,
            orderItems
          )
        );

        setCart(current);
      } catch (error) {
        sheetFailed('shippingContact', 'Could not price the Apple Pay shipping address', error);
        tellSheet(() =>
          session.completeShippingContactSelection(
            window.ApplePaySession.STATUS_INVALID_SHIPPING_CONTACT,
            [],
            totalFor(current),
            buildOrderItems(current)
          )
        );
      }
    };

    session.onshippingmethodselected = async (event: any) => {
      let current: PaymentCart = { ...cartRef.current };

      try {
        current = await calculateShipping(current, current.address!, event.shippingMethod.identifier);
        current = await calculateTaxes(current, current.address!);

        const total = totalFor(current);
        const orderItems = buildOrderItems(current);

        tellSheet(() =>
          session.completeShippingMethodSelection(window.ApplePaySession.STATUS_SUCCESS, total, orderItems)
        );

        setCart(current);
      } catch (error) {
        sheetFailed('shippingMethod', 'Could not price the Apple Pay shipping method', error);
        tellSheet(() =>
          session.completeShippingMethodSelection(
            window.ApplePaySession.STATUS_FAILURE,
            totalFor(current),
            buildOrderItems(current)
          )
        );
      }
    };

    session.onpaymentauthorized = async (event: any) => {
      const current = cartRef.current;

      try {
        const tokenized = await applePayInstance.tokenize({ token: event.payment.token });
        const contact = event.payment;

        const payload = {
          ...tokenized,
          // Top level, not under `details`: public/gateways/braintree.php reads
          // `$payload->deviceData` off the root when it charges the nonce, which is
          // where the legacy jQuery button puts it. headless nested it, and Braintree's
          // fraud tools have been getting nothing from those orders ever since.
          //
          // Awaited here rather than before the button was drawn: this is the first
          // moment the value is actually wanted, and by now it has had the whole visit
          // to arrive.
          deviceData: await deviceData.current,
          details: {
            ...tokenized.details,
            billingAddress: toPayloadAddress(contact.billingContact),
            shippingAddress: toPayloadAddress(contact.shippingContact),
            email: contact.shippingContact?.emailAddress,
            shippingOptionId: current.selected_shipping_option?.id ?? null,
          },
        };

        // Told before the cart write, not after: the sheet is modal and the visitor is
        // watching a spinner until it is dismissed, and the cart write is followed by a
        // full navigation to checkout.
        endSheet();
        // Guarded like the rest, and here it is the order that depends on it: a throw
        // dismissing the sheet used to skip the cart write below and lose a payment the
        // visitor had already authorized.
        tellSheet(() => session.completePayment(window.ApplePaySession.STATUS_SUCCESS));

        await submitPayload(JSON.stringify(payload));
      } catch (error) {
        sheetFailed('paymentAuthorized', 'Could not complete the Apple Pay payment', error);
        endSheet();
        tellSheet(() => session.completePayment(window.ApplePaySession.STATUS_FAILURE));
      }
    };

    try {
      session.begin();
    } catch (error) {
      // The guard above covers this button. It cannot cover a session this page opened
      // some other way — the rule is per document, not per button, so a sheet belonging
      // to another wallet in the same row would refuse this one too. Nothing here can end
      // a sheet it does not own, so the guard is released and the visitor can tap again
      // once that sheet is gone.
      //
      // Logged rather than thrown because this handler is async: an uncaught throw here
      // becomes an unhandled rejection, which is exactly how this reached Airbrake.
      endSheet();
      console.error('Could not open the Apple Pay sheet', error);
    }
  }

  if (!isReady || !reveal.isRevealed) {
    return null;
  }

  return (
    <Button
      color="apple-pay"
      fullWidth
      {...BUTTON_CORNERS[shape]}
      onClick={payWithApplePay}
      ariaLabel="Buy with Apple Pay"
    >
      {!compact && 'Buy with '}
      <FontAwesomeIcon icon={faApple} /> Pay
    </Button>
  );
}

/** Apple's contact shape, in the shape checkout stores an address in. */
function toPayloadAddress(contact: any) {
  if (!contact) {
    return undefined;
  }

  return {
    forename: contact.givenName,
    surname: contact.familyName,
    line1: contact.addressLines?.[0],
    line2: contact.addressLines?.[1],
    city: contact.locality,
    state: contact.administrativeArea,
    postalCode: contact.postalCode,
    countryCode: String(contact.countryCode || '').toUpperCase(),
  };
}
